import { gql } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import classnames from 'classnames';
import { groupBy, partition } from 'lodash';
import React, { CSSProperties } from 'react';

import { BaseChangeIcon } from 'src/app/graph/changeLogPage/changeIcon/ChangeIcon';
import { TypeOfChange } from 'src/app/graph/changeLogPage/getTypeOfChangeByChangeCodeAndTypeOfThing';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const ChangeListChangeOnOperationFragment = gql`
  fragment ChangeListChangeOnOperationFragment on ChangeOnOperation {
    semanticChange {
      shortDescription
      definition {
        code
        category
      }
      parentNode {
        name
        kind
      }
      childNode {
        name
        printedType
      }
      argNode {
        name
      }
    }
    impact
  }
`;

interface Props {
  changes: GraphQLTypes.ChangeListChangeOnOperationFragment[];
}

export const ChangeList = ({ changes }: Props) => {
  const changesGroupedByParentName = groupBy(
    changes,
    (change) => change.semanticChange.parentNode?.name ?? '',
  );
  return (
    <div className="flex flex-col items-start">
      {Object.entries(changesGroupedByParentName).map(
        ([parentName, changesForParentName]) =>
          parentName && (
            <ChangeParentSection
              key={parentName}
              parentName={parentName}
              parentKind={
                changesForParentName[0]?.semanticChange.parentNode?.kind
              }
              changes={changesForParentName}
            />
          ),
      )}
    </div>
  );
};

// This is a section in a change list that renders the set of changes for a
// single parentNode, which represents a GraphQL type (object, input object,
// interface, union, scalar, enum).
const ChangeParentSection = ({
  parentName,
  parentKind,
  changes,
}: {
  parentName: string;
  parentKind?: GraphQLTypes.IntrospectionTypeKind | null;
  changes: GraphQLTypes.ChangeListChangeOnOperationFragment[];
}) => {
  // We partition the changes on whether they apply directly to the parent or a
  // descendant. We can determine this by observing whether there's a childNode
  // at all for the change.
  const [parentChanges, descendantChanges] = partition(
    changes,
    (change) => !change.semanticChange.childNode,
  );
  // There are cases where parentChanges may have multiple elements.
  const [primaryParentChanges, secondaryParentChanges] = partition(
    parentChanges,
    (change) =>
      PRIMARY_PARENT_CHANGE_CODES.has(change.semanticChange.definition.code),
  );
  const remainingParentChanges = primaryParentChanges.concat(
    secondaryParentChanges,
  );
  const firstParentChange = remainingParentChanges.shift();
  const descendantChangesGroupedByChildName = groupBy(
    descendantChanges,
    (change) => change.semanticChange.childNode?.name ?? '',
  );
  return (
    <>
      {(descendantChanges.length > 0 || firstParentChange) && (
        <ChangeParentItem
          parentName={parentName}
          parentKind={parentKind}
          change={firstParentChange}
        />
      )}
      {descendantChanges.length > 0 && (
        <div className="mb-1 ml-5 last:mb-0">
          {Object.entries(descendantChangesGroupedByChildName).map(
            ([childName, changesForChildName]) =>
              childName && (
                <ChangeChildSection
                  key={childName}
                  childName={childName}
                  childPrintedType={
                    changesForChildName[0]?.semanticChange.childNode
                      ?.printedType
                  }
                  changes={changesForChildName}
                />
              ),
          )}
        </div>
      )}
      {remainingParentChanges.map((change) => (
        <ChangeParentItem
          key={change.semanticChange.definition.code}
          parentName={parentName}
          parentKind={parentKind}
          change={change}
        />
      ))}
    </>
  );
};

// For a given parentNode, at most one of these codes should apply in a diff.
const PRIMARY_PARENT_CHANGE_CODES = new Set([
  'TYPE_ADDED',
  'TYPE_REMOVED',
  'TYPE_CHANGED_KIND',
]);

const ChangeParentItem = ({
  parentName,
  parentKind,
  change,
}: {
  parentName: string;
  parentKind?: GraphQLTypes.IntrospectionTypeKind | null;
  change?: GraphQLTypes.ChangeListChangeOnOperationFragment;
}) => {
  return (
    <div className="mb-1 flex items-center gap-2">
      <ChangeIcon changeCategory={change?.semanticChange.definition.category} />
      <ChangeHighlightedText
        changeCategory={change?.semanticChange.definition.category}
        text={parentName}
      />
      <span className="whitespace-nowrap text-sm text-secondary">
        {change
          ? changeDescription({
              code: change.semanticChange.definition.code,
              parentKind,
              shortDescription: change.semanticChange.shortDescription,
            })
          : parentKind && INTROSPECTION_TYPE_KIND_TO_TEXT[parentKind]
            ? `${INTROSPECTION_TYPE_KIND_TO_TEXT[parentKind]} type modified`
            : 'type modified'}
      </span>
      {change?.impact && (
        <Tooltip label={change.impact}>
          <IconInfo className="text-icon-primary" />
        </Tooltip>
      )}
    </div>
  );
};

// This is a section in a change list that renders the set of changes for a
// single childNode, which represents a GraphQL field, enum value, or member
// of an interface/union.
const ChangeChildSection = ({
  childName,
  childPrintedType,
  changes,
}: {
  childName: string;
  childPrintedType?: string | null;
  changes: GraphQLTypes.ChangeListChangeOnOperationFragment[];
}) => {
  // We partition the changes on whether they apply directly to the child or a
  // descendant. We can determine this by observing whether there's an argNode
  // at all for the change.
  const [childChanges, descendantChanges] = partition(
    changes,
    (change) => !change.semanticChange.argNode,
  );
  // There are cases where childChanges may have multiple elements.
  const [primaryChildChanges, secondaryChildChanges] = partition(
    childChanges,
    (change) =>
      PRIMARY_CHILD_CHANGE_CODES.has(change.semanticChange.definition.code),
  );
  const remainingChildChanges = primaryChildChanges.concat(
    secondaryChildChanges,
  );
  const firstChildChange = remainingChildChanges.shift();
  const descendantChangesGroupedByArgName = groupBy(
    descendantChanges,
    (change) => change.semanticChange.argNode?.name ?? '',
  );
  return (
    <>
      {(descendantChanges.length > 0 || firstChildChange) && (
        <ChangeChildItem
          childName={childName}
          childPrintedType={childPrintedType}
          change={firstChildChange}
        />
      )}
      {descendantChanges.length > 0 && (
        <div className="ml-5">
          {Object.entries(descendantChangesGroupedByArgName).map(
            ([argName, changesForArgName]) =>
              argName && (
                <ChangeArgSection
                  key={argName}
                  argName={argName}
                  changes={changesForArgName}
                />
              ),
          )}
        </div>
      )}
      {remainingChildChanges.map((change) => (
        <ChangeChildItem
          key={change.semanticChange.definition.code}
          childName={childName}
          childPrintedType={childPrintedType}
          change={change}
        />
      ))}
    </>
  );
};

// For a given childNode, at most one of these codes should apply in a diff.
const PRIMARY_CHILD_CHANGE_CODES = new Set([
  // Object and interface fields
  'FIELD_ADDED',
  'FIELD_REMOVED',
  'FIELD_CHANGED_TYPE',
  'FIELD_CHANGED_KIND', // deprecated
  // Input object fields
  'NON_NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT', // deprecated
  'REQUIRED_FIELD_ADDED_TO_INPUT_OBJECT',
  'NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT', // deprecated
  'OPTIONAL_FIELD_ADDED_TO_INPUT_OBJECT',
  'FIELD_REMOVED_FROM_INPUT_OBJECT',
  'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE',
  'NON_NULL_INPUT_FIELD_ADDED', // deprecated
  'NULLABLE_INPUT_FIELD_ADDED', // deprecated
  'INPUT_FIELD_REMOVED', // deprecated
  'NULLABLE_FIELD_ADDED_TO_INPUT_OBJECT ', // deprecated, note the trailing space
  // Enum values
  'VALUE_ADDED_TO_ENUM',
  'VALUE_REMOVED_FROM_ENUM',
  'ENUM_VALUE_ADDED', // deprecated
  'ENUM_VALUE_REMOVED', // deprecated
  // Interface membership
  'TYPE_ADDED_TO_INTERFACE',
  'TYPE_REMOVED_FROM_INTERFACE',
  'INTERFACE_ADDED_TO_OBJECT', // deprecated
  'INTERFACE_REMOVED_FROM_OBJECT', // deprecated
  // Union membership
  'TYPE_ADDED_TO_UNION',
  'TYPE_REMOVED_FROM_UNION',
]);

const ChangeChildItem = ({
  childName,
  childPrintedType,
  change,
}: {
  childName: string;
  childPrintedType?: string | null;
  change?: GraphQLTypes.ChangeListChangeOnOperationFragment;
}) => {
  return (
    <div className="mb-1 flex items-center gap-2">
      <ChangeIcon changeCategory={change?.semanticChange.definition.category} />
      {change?.semanticChange.definition.code &&
      childPrintedType &&
      HIGHLIGHT_TYPE_ONLY_CHILD_CHANGE_CODES.has(
        change.semanticChange.definition.code,
      ) ? (
        <>
          <ChangeHighlightedText text={`${childName}: `} />
          <ChangeHighlightedText
            changeCategory={change.semanticChange.definition.category}
            text={childPrintedType}
          />
        </>
      ) : (
        <ChangeHighlightedText
          changeCategory={change?.semanticChange.definition.category}
          text={
            childPrintedType ? `${childName}: ${childPrintedType}` : childName
          }
        />
      )}
      <span className="whitespace-nowrap text-sm text-secondary">
        {change?.semanticChange.definition.code
          ? changeDescription({
              code: change.semanticChange.definition.code,
              shortDescription: change.semanticChange.shortDescription,
            })
          : 'field modified'}
      </span>
      {change?.impact && (
        <Tooltip label={change.impact}>
          <IconInfo className="text-icon-primary" />
        </Tooltip>
      )}
    </div>
  );
};

const HIGHLIGHT_TYPE_ONLY_CHILD_CHANGE_CODES = new Set([
  // Object and interface field types
  'FIELD_CHANGED_TYPE',
  'FIELD_CHANGED_KIND', // deprecated
  // Input object field types
  'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE',
]);

// This is a section in a change list that renders the set of changes for a
// single argNode, which represents a GraphQL field argument.
const ChangeArgSection = ({
  argName,
  changes,
}: {
  argName: string;
  changes: GraphQLTypes.ChangeListChangeOnOperationFragment[];
}) => {
  // There are cases where argument changes may have multiple elements.
  const [primaryArgChanges, secondaryArgChanges] = partition(
    changes,
    (change) =>
      PRIMARY_ARG_CHANGE_CODES.has(change.semanticChange.definition.code),
  );
  const orderedArgChanges = primaryArgChanges.concat(secondaryArgChanges);
  return (
    <>
      {orderedArgChanges.map((change) => (
        <div
          key={change.semanticChange.definition.code}
          className="mb-1 flex items-center gap-2"
        >
          <ChangeIcon
            changeCategory={change?.semanticChange.definition.category}
          />
          <ChangeHighlightedText
            changeCategory={change.semanticChange.definition.category}
            text={argName}
          />
          <span className="whitespace-nowrap text-sm text-secondary">
            {changeDescription({
              code: change.semanticChange.definition.code,
              shortDescription: change.semanticChange.shortDescription,
            })}
          </span>
          {change?.impact && (
            <Tooltip label={change.impact}>
              <IconInfo className="text-icon-primary" />
            </Tooltip>
          )}
        </div>
      ))}
    </>
  );
};

// For a given argNode, at most one of these codes should apply in a diff.
const PRIMARY_ARG_CHANGE_CODES = new Set([
  'OPTIONAL_ARG_ADDED',
  'REQUIRED_ARG_ADDED',
  'ARG_REMOVED',
  'ARG_CHANGED_TYPE',
  'ARG_CHANGED_TYPE_OPTIONAL_TO_REQUIRED',
  'ARG_ADDED', // deprecated
  'ARG_CHANGED_KIND', // deprecated
]);

const ChangeIcon = ({
  changeCategory,
  className,
}: {
  changeCategory?: GraphQLTypes.ChangeCategory;
  className?: string;
}) => {
  const typeOfChange = changeCategory
    ? CHANGE_CATEGORY_TO_TYPE_OF_CHANGE[changeCategory]
    : 'SOMETHING_INSIDE_THIS_CHANGED';
  return (
    <BaseChangeIcon
      changeType={typeOfChange}
      className={className}
      style={getChangeIconStyles(typeOfChange)}
    />
  );
};

const getChangeIconStyles = (
  changeType: TypeOfChange | null,
): CSSProperties => {
  switch (changeType) {
    case 'ADDITION':
      return {
        width: 16,
        minWidth: 16,
        height: 16,
      };
    case 'REMOVAL':
      return {
        width: 16,
        minWidth: 16,
        height: 16,
      };
    case 'DEPRECATION':
      return {
        width: 16,
        minWidth: 16,
        height: 16,
      };
    case 'THIS_CHANGED':
      return {
        width: 16,
        minWidth: 16,
        height: 16,
      };
    case 'SOMETHING_INSIDE_THIS_CHANGED':
    default:
      return {
        width: 16,
        minWidth: 16,
        height: 16,
      };
  }
};

const CHANGE_CATEGORY_TO_TYPE_OF_CHANGE: Record<
  GraphQLTypes.ChangeCategory,
  TypeOfChange
> = {
  ADDITION: 'ADDITION',
  REMOVAL: 'REMOVAL',
  EDIT: 'THIS_CHANGED',
  DEPRECATION: 'DEPRECATION',
};

const ChangeHighlightedText = ({
  changeCategory,
  text,
}: {
  changeCategory?: GraphQLTypes.ChangeCategory;
  text: string;
}) => {
  const borderedClassname = classnames('rounded border px-2');
  return (
    <span
      className={classnames(
        'whitespace-nowrap font-mono text-base',
        // If you're wondering why this isn't a Record or a switch block in a
        // function, it's because we explicitly disallow this for classnames,
        // because otherwise local/strict-class-names won't be applied.
        changeCategory === 'ADDITION'
          ? classnames(
              // eslint-disable-next-line local/no-conditional-class-names
              borderedClassname,
              'border-success bg-success text-success',
            )
          : changeCategory === 'REMOVAL'
            ? // eslint-disable-next-line local/no-conditional-class-names
              classnames(borderedClassname, 'border-error bg-error text-error')
            : changeCategory === 'EDIT'
              ? classnames(
                  // eslint-disable-next-line local/no-conditional-class-names
                  borderedClassname,
                  'border-warning bg-warning text-warning',
                )
              : changeCategory === 'DEPRECATION'
                ? classnames(
                    // eslint-disable-next-line local/no-conditional-class-names
                    borderedClassname,
                    'border-warning bg-warning text-warning',
                  )
                : '',
      )}
    >
      {text}
    </span>
  );
};

function changeDescription({
  code,
  parentKind,
  shortDescription,
}: {
  code: string;
  parentKind?: GraphQLTypes.IntrospectionTypeKind | null;
  shortDescription?: string | null;
}): string {
  // Return overridden short description if it exists on the change
  if (shortDescription) {
    return shortDescription;
  }

  const parentKindText =
    parentKind && INTROSPECTION_TYPE_KIND_TO_TEXT[parentKind];
  switch (code) {
    case 'TYPE_ADDED':
      return parentKindText ? `${parentKindText} type added` : 'type added';
    case 'TYPE_REMOVED':
      return parentKindText ? `${parentKindText} type removed` : 'type removed';
    case 'TYPE_CHANGED_KIND':
      return parentKindText
        ? `type changed kind to ${parentKindText}`
        : 'type changed kind';
    case 'TYPE_DESCRIPTION_CHANGE':
      return parentKindText
        ? `${parentKindText} type description change`
        : 'type description change';
    default:
      return code
        .trim()
        .toLowerCase()
        .split('_')
        .map((word) => (word === 'arg' ? 'argument' : word))
        .join(' ');
  }
}

const INTROSPECTION_TYPE_KIND_TO_TEXT: Record<
  GraphQLTypes.IntrospectionTypeKind,
  string | null
> = {
  OBJECT: 'object',
  INPUT_OBJECT: 'input object',
  INTERFACE: 'interface',
  UNION: 'union',
  SCALAR: 'scalar',
  ENUM: 'enum',
  LIST: null,
  NON_NULL: null,
};
